// Contact.jsx
// Page with contact information and map
// NOTE: You'll need to update the map embed URL with the correct one from Google Maps

import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/Contact.css';

function Contact() {
  const { language, t } = useLanguage();

  // WhatsApp link - changes message based on language
  // Template literals (backticks) allow us to insert variables into strings
  const whatsappLink = language === 'en' 
    ? "https://wa.me/919011044937?text=Hi,%20I'm%20interested%20in%20your%20services"
    : "https://wa.me/919011044937?text=नमस्कार,%20मला%20तुमच्या%20सेवांमध्ये%20स्वारस्य%20आहे";

  return (
    <div className="contact-page">
      <section className="contact">
        {/* Page Header */}
        <h2 className="section-title">{t.contact.title}</h2>
        <p className="section-subtitle">{t.contact.subtitle}</p>
        
        {/* Contact Container - Split into Info and Map */}
        <div className="contact-container">
          {/* Left Side - Contact Information */}
          <div className="contact-info">
            <h3>{t.contact.getInTouch}</h3>
            
            {/* Address */}
            <div className="info-item">
              <div className="info-icon">📍</div>
              <div className="info-content">
                <h4>{t.contact.address}</h4>
                {/*
                  PRESERVING LINE BREAKS:
                  \n in the text creates line breaks
                  whiteSpace: 'pre-line' tells CSS to respect them
                */}
                <p style={{ whiteSpace: 'pre-line' }}>
                  {t.contact.addressText}
                </p>
              </div>
            </div>

            {/* Phone */}
            <div className="info-item">
              <div className="info-icon">📞</div>
              <div className="info-content">
                <h4>{t.contact.phone}</h4>
                <p>
                  {/* 
                    tel: protocol makes phone numbers clickable on mobile
                    When clicked, it opens the phone app
                  */}
                  <a href="tel:+919850488855">+91 98504 88855</a><br />
                  <a href="tel:+919011044937">+91 90110 44937</a>
                </p>
              </div>
            </div>

            {/* Working Hours */}
            <div className="info-item">
              <div className="info-icon">🕐</div>
              <div className="info-content">
                <h4>{t.contact.workingHours}</h4>
                <p style={{ whiteSpace: 'pre-line' }}>
                  {t.contact.workingHoursText}
                </p>
              </div>
            </div>

            {/* WhatsApp Button */}
            {/*
              CONDITIONAL RENDERING OF LINKS:
              The whatsappLink variable changes based on language
              So the message that gets sent changes too!
            */}
            <a 
              href={whatsappLink}
              target="_blank" 
              rel="noopener noreferrer"
              className="whatsapp-btn"
            >
              <span style={{ fontSize: '1.3rem' }}>💬</span>
              {t.contact.whatsapp}
            </a>
          </div>

          {/* Right Side - Google Maps */}
          <div className="map-container">
            {/*
              ⚠️ IMPORTANT - TO UPDATE THE MAP:
              
              1. Go to: https://maps.app.goo.gl/6bKCEpsP76dcc7bx9
              2. Click "Share" button
              3. Click "Embed a map" tab
              4. Copy the <iframe> code
              5. Replace the src URL below with the one from Google
              
              The current URL is a placeholder!
            */}
            {/* <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3780.7664471224903!2d73.77678787595832!3d18.62957176583414!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2b9bb08120057%3A0x98d09e560fc1f1b2!2sSaaj%20Fashion%20Art!5e0!3m2!1sen!2sie!4v1769890537982!5m2!1sen!2sie" width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe> */}
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3780.7664471224903!2d73.77678787595832!3d18.62957176583414!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2b9bb08120057%3A0x98d09e560fc1f1b2!2sSaaj%20Fashion%20Art!5e0!3m2!1sen!2sie!4v1769890537982!5m2!1sen!2sie"
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Saaj Fashion Art Location"
            >
            </iframe>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;

/*
NEW CONCEPTS IN THIS COMPONENT:

1. CONDITIONAL VALUES:
   const whatsappLink = language === 'en' ? 'url1' : 'url2'
   
   This is a ternary operator:
   condition ? valueIfTrue : valueIfFalse
   
   Like an if-else in one line!

2. TEMPLATE LITERALS:
   `string with ${variable}`
   
   Use backticks (`) instead of quotes
   Can insert variables with ${variableName}
   Example: `Hello ${name}` becomes "Hello John"

3. SPECIAL URL PROTOCOLS:
   - tel:+123456789 - Opens phone dialer
   - mailto:email@example.com - Opens email app
   - https://wa.me/number - Opens WhatsApp
   
   These make the site mobile-friendly!

4. IFRAME:
   <iframe> embeds another webpage inside yours
   Used here to embed Google Maps
   
   Important attributes:
   - src: The URL to embed
   - allowFullScreen: Allows fullscreen mode
   - loading="lazy": Loads only when scrolled to (performance!)
   - title: For accessibility

5. TARGET & REL ATTRIBUTES:
   target="_blank" - Opens in new tab
   rel="noopener noreferrer" - Security measure
   
   Always use both together when opening external links!
*/
